"use client";

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

/**
 * Titre de contexte affiché dans la barre du haut, à droite de la recherche.
 *
 * Le lecteur de formation ne nommait nulle part la formation en cours, et
 * toute bande ajoutée au-dessus de la vidéo décalait celle-ci et cassait son
 * alignement avec le sommaire. Le nom passe donc dans l'en-tête, qui existe
 * déjà et dont le centre est vide : zéro pixel pris à la vidéo.
 *
 * Une page pose son titre avec useHeaderContextTitle ; toutes les autres n'en
 * posent aucun, et l'en-tête reste exactement ce qu'il était.
 */
const HeaderContextTitleContext = createContext(null);

export function HeaderContextTitleProvider({ children }) {
    // { title, href } ou null
    const [contextTitle, setContextTitle] = useState(null);

    const value = useMemo(
        () => ({ contextTitle, setContextTitle }),
        [contextTitle],
    );

    return (
        <HeaderContextTitleContext.Provider value={value}>
            {children}
        </HeaderContextTitleContext.Provider>
    );
}

/** Lu par la barre du haut. */
export function useHeaderContextTitleValue() {
    return useContext(HeaderContextTitleContext)?.contextTitle || null;
}

/**
 * Posé par une page. Le titre disparaît de lui-même quand on quitte la page,
 * pour qu'il ne survive jamais à l'écran qui l'a posé.
 *
 * @param {string|null} title Nom à afficher, null pour ne rien afficher.
 * @param {object|string|null} href Destination du lien (objet next-intl).
 */
export function useHeaderContextTitle(title, href = null) {
    const ctx = useContext(HeaderContextTitleContext);
    const setContextTitle = ctx?.setContextTitle;

    // href est souvent un objet reconstruit à chaque rendu : on le compare sur
    // sa forme sérialisée pour ne pas relancer l'effet à chaque frappe.
    const hrefKey = href ? JSON.stringify(href) : "";

    const apply = useCallback(() => {
        if (!setContextTitle) return undefined;
        if (!title) {
            setContextTitle(null);
            return undefined;
        }
        setContextTitle({ title, href: hrefKey ? JSON.parse(hrefKey) : null });
        return () => setContextTitle(null);
    }, [setContextTitle, title, hrefKey]);

    useEffect(apply, [apply]);
}
